// What the open-ended family knows about a question beyond its payload: which view modes it can
// offer, which one it opens on, and the sentiment / codebook / tag universe those modes draw from.
// Ported from the coffee lib the shell used to hand across as a prop.
//
// The combo, conversation and menu-test blocks each build their own from `calcFamilyLib` - the
// options list and the rule that decides which of them survive are what differ; everything
// underneath is shared.

import { getFeature, getModelValue, getSurveyCodeBooks, getSurveyConfig, isReportXpert } from '@aytm/app/stats/lib/stat-page'
import { questionIdToPosition, questionPositionToOldId } from '@aytm/app/stats/page/components/lib/utils'

// a verbatim config can pin the block to another question's answer; the key then names that one
const LINKED_IDS_REG = /^([pv]?q\d+)a(\d+)$/i

// tag paths are named after the question, so a bare id must not swallow a longer one
const NOT_A_LONGER_ID = '(?!\\d+)'

// The answer paths this question's tags are filed under: the question's own old id, narrowed to
// one answer or one named part for a sub-item.
const pathRegExp = (key, boundary = '') => {
  const path = String(key).split('.')
  const id = questionPositionToOldId(path[0])

  if (undefined === path[1]) return new RegExp(`^${id}${boundary}`)

  const part = Number.isNaN(Number(path[1])) ? path[1] : `[ab]${path[1]}`

  return new RegExp(`^${id}${boundary}.${part}${boundary}`)
}

const isTagsViewOff = () => Boolean(getFeature('oe_tags_view')?.disabled)
const isSentimentsViewOff = () => Boolean(getFeature('oe_sentiments_view')?.disabled)

const getTagsStats = () => getModelValue('tagsData')?.tagsStats || {}

// the report's own tag settings on top of the survey's
const getReportTags = () => Object.assign({}, getSurveyConfig('tags') || {}, getModelValue('tags') || {})

// the two-column open-ended variant carries a cloud and nothing else
const isCloudOnly = type => 'oex-2-col' === type

export const getKey = (key, config = {}) => {
  const match = Object.hasOwn(config, 'ids') ? String(config.ids ?? '').match(LINKED_IDS_REG) : null
  if (!match) return key

  return `${questionPositionToOldId(questionIdToPosition(match[1]))}.a${match[2]}`
}

// The codebooks with at least one tag actually applied to this question's answers, oldest first.
export const getQuestionCodeBooks = (key, type) => {
  if (isCloudOnly(type) || isTagsViewOff()) return []

  const tagsStats = getTagsStats()
  const reg = pathRegExp(key, NOT_A_LONGER_ID)

  return getSurveyCodeBooks()
    .filter(codebook => {
      const codeBookTags = codebook.tags.map(tag => `${codebook.title}: ${tag}`)

      return Object.keys(tagsStats).some(
        path =>
          reg.test(path) &&
          0 < (tagsStats[path]?.[codebook.type]?.filter(tag => codeBookTags.includes(tag.tag)).length || 0)
      )
    })
    .sort((a, b) => a.ts - b.ts)
}

export const getQuestionTags = (key, type, codebook = null) => {
  if (isCloudOnly(type) || isTagsViewOff() || null == codebook) return []

  return getQuestionCodeBooks(key, type).find(cb => cb.title === codebook)?.tags || []
}

export const getQuestionSentiments = (key, type) => {
  if (isCloudOnly(type) || isSentimentsViewOff()) return []

  const tags = getReportTags()
  const tagsStats = getTagsStats()
  const reg = pathRegExp(key, NOT_A_LONGER_ID)

  return Object.keys(tagsStats).reduce((acc, path) => {
    let names = acc

    if (reg.test(path) && false !== tags?.sentiment?.[path] && tagsStats[path]?.sentiment) {
      names = names.concat(tagsStats[path].sentiment.map(v => v.tag).filter(v => 'None' !== v))
    }

    return names.filter((v, i, all) => null != v && all.indexOf(v) === i)
  }, [])
}

// The tag and sentiment names this question carries, which is what makes those view modes worth
// offering at all. A view the report has switched off contributes nothing.
const calcTagNames = key => {
  const tags = getReportTags()
  const tagsStats = getTagsStats()
  const reg = pathRegExp(key)

  const tagsOff = isTagsViewOff()
  const sentimentsOff = isSentimentsViewOff()

  return Object.keys(tagsStats).reduce(
    (acc, path) => {
      if (!reg.test(path) || null == tagsStats[path]) return acc

      if (!tagsOff && tagsStats[path].manual) acc.manual = acc.manual.concat(tagsStats[path].manual.map(v => v.tag))

      if (!sentimentsOff && false !== tags?.sentiment?.[path] && tagsStats[path].sentiment) {
        acc.sentiment = acc.sentiment.concat(tagsStats[path].sentiment.map(v => v.tag).filter(v => 'None' !== v))
      }

      return acc
    },
    { manual: [], sentiment: [] }
  )
}

// A quote view needs individual data switched on for open answers.
export const isVerbatimOffered = () => 'off' !== getFeature('individual_data')?.status?.oe

// A family is its options list plus the rule that says which of them this question can answer;
// `getDefaultViewMode` opens on the first that survives, so the two always agree.
export const calcFamilyLib = ({ viewModesOptions, presetOptions, defaultConfig, isOffered }) => {
  const getViewModesOptions = (key, type, { viewModes } = {}) => {
    if (isCloudOnly(type)) return [{ key: 'wordcloud', text: 'Wordcloud' }]

    const context = { key, type, tagNames: calcTagNames(key), codeBooks: getQuestionCodeBooks(key, type) }
    const xpert = isReportXpert()

    return (
      viewModesOptions
        .filter(v => (0 < viewModes?.length ? viewModes.includes(v.key) : true))
        // an Xpert report shows the answers themselves, never what was read out of them
        .filter(v => ['wordcloud', 'verbatim'].includes(v.key) || !xpert)
        .filter(v => isOffered(v.key, context))
    )
  }

  const getDefaultViewMode = (key, type, { viewModes, viewMode } = {}) => {
    if (null != viewMode) return viewMode

    return (isCloudOnly(type) && 'wordcloud') || getViewModesOptions(key, type, { viewModes })[0].key
  }

  return {
    viewModesOptions,
    presetOptions,
    defaultConfig,
    getKey,
    getQuestionCodeBooks,
    getQuestionTags,
    getQuestionSentiments,
    getViewModesOptions,
    getDefaultViewMode
  }
}

export const OE_VIEW_MODES_OPTIONS = [
  { key: 'verbatim', text: 'Quote tiles' },
  { key: 'sentiments', text: 'Sentiments' },
  { key: 'tags', text: 'Codebooks' },
  { key: 'wordcloud', text: 'Wordcloud' }
]

export const OE_PRESET_OPTIONS = [
  { key: 'longest', text: 'Longest' },
  { key: 'median', text: 'Median length' },
  { key: 'shortest', text: 'Shortest' }
]

export const OE_DEFAULT_CONFIG = { verbatim: { preset: 'median', search: { query: [] } } }

// What the coffee block class carried: the modes that draw a chart, and that the family heals.
export const OE_CHART_VIEW_MODES = ['sentiments', 'tags']
export const OE_HEALS_VIEW_MODE = true

export const isOeViewModeOffered = (mode, { tagNames, codeBooks }) => {
  if ('wordcloud' === mode) return true
  if ('verbatim' === mode) return isVerbatimOffered()
  if ('sentiments' === mode) return 0 < tagNames.sentiment.length
  if ('tags' === mode) return 0 < codeBooks.length && 0 < tagNames.manual.length

  return false
}

export const OE_FAMILY_LIB = calcFamilyLib({
  viewModesOptions: OE_VIEW_MODES_OPTIONS,
  presetOptions: OE_PRESET_OPTIONS,
  defaultConfig: OE_DEFAULT_CONFIG,
  isOffered: isOeViewModeOffered
})
